import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, AlertTriangle, RotateCcw, LogOut, Info, Camera, SunMedium } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { isStripePublishableKeyConfigured } from "@/lib/stripe/env";
import { getStripePromise } from "@/lib/stripe/stripe";
import {
  isUsableErrorMessage,
  paymentErrorMessage,
} from "@/lib/stripe/payment-errors";
import { reportPaymentError } from "@/lib/stripe/telemetry";

/**
 * IdentityVerificationModal — Gate de KYC con Stripe Identity.
 *
 * Flujo:
 *  1. Edge Function `create-identity-verification` crea/reusa una
 *     VerificationSession `type='document'` y devuelve `client_secret`.
 *  2. `stripe.verifyIdentity(clientSecret)` abre el modal de Stripe (iframe)
 *     para capturar el DOCUMENTO (DNI/pasaporte). Sin selfie: solo documento,
 *     tal y como requiere Stripe Identity.
 *  3. El webhook `verification_session.verified` marca `users.identity_verified`.
 *  4. Este modal hace polling ligero de `create-identity-verification` (que
 *     devuelve `already_verified`) hasta confirmar, refresca el perfil y cierra.
 *
 * El gate es NO-dismissible: el usuario debe verificarse para operar.
 */
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "";

type Phase = "loading" | "ready" | "verifying" | "success" | "error" | "aborted";

interface IdentityVerificationModalProps {
  open: boolean;
}

interface IdentityVerificationLastError {
  code?: string;
  reason?: string;
}

interface PollResult {
  verified: boolean;
  lastError?: IdentityVerificationLastError | null;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Polling loop para confirmar que el webhook de Stripe ha marcado
 * identity_verified = true en la BD.
 * 
 * Se ejecuta tanto en flujo estándar (verifyIdentity sin errores)
 * como en flujo cross-device (verifyIdentity retorna canceled/requires_input).
 * 
 * MEJORADA (2026-09-14): timeout aumentado de 30s → 100s para tolerar webhooks lentos.
 * (documentación del incidente eliminada del repo 2026-09-15)
 * 
 * @returns { verified: boolean, lastError?: IdentityVerificationLastError | null }
 */
async function pollForIdentityVerification(opts?: { maxIterations?: number; intervalMs?: number }): Promise<PollResult> {
  const maxIterations = opts?.maxIterations ?? 40; // 40 × 2.5s = 100s (antes era 20 × 1.5s = 30s)
  const intervalMs = opts?.intervalMs ?? 2500;
  let detectedLastError: IdentityVerificationLastError | null = null;
  
  for (let i = 0; i < maxIterations; i += 1) {
    await sleep(intervalMs);
    try {
      const { data: check } = await supabase.functions.invoke("create-identity-verification", {
        body: {
          require_biometric: true,
          level: "biometric",
        },
      });
      if (check?.already_verified) {
        console.log(`[pollForIdentityVerification] ✅ Verified at iteration ${i + 1}/${maxIterations}`);
        return { verified: true };
      }
      if (check?.last_error) {
        detectedLastError = check.last_error;
      }
    } catch (err) {
      console.warn(`[pollForIdentityVerification] Check iteration ${i + 1} failed, retrying...`, err);
    }
  }
  console.warn(`[pollForIdentityVerification] ⏱️ Polling expired after ${maxIterations} iterations (~${maxIterations * intervalMs / 1000}s)`);
  return { verified: false, lastError: detectedLastError };
}

/**
 * Helper para traducir y dar consejos accionables según el código de error de Stripe Identity
 */
function getIdentityErrorHelp(lastError?: IdentityVerificationLastError | null) {
  if (!lastError?.code) return null;

  switch (lastError.code) {
    case "document_unverified_other":
      return {
        title: "Problema de legibilidad en el documento",
        description: "Stripe no pudo leer con claridad los datos de tu documento. Esto suele ocurrir por falta de nitidez, iluminación insuficiente o reflejos.",
        tips: [
          "Evita reflejos de bombillas directas o del flash sobre el plástico del documento.",
          "Coloca el documento sobre una superficie plana, uniforme y de fondo oscuro.",
          "Asegúrate de que las 4 esquinas del documento sean visibles y el texto esté bien enfocado.",
        ],
      };
    case "document_expired":
      return {
        title: "Documento caducado",
        description: "El documento presentado ha expirado.",
        tips: [
          "Utiliza un documento de identidad o pasaporte en vigor.",
        ],
      };
    case "document_type_not_supported":
      return {
        title: "Tipo de documento no admitido",
        description: "El formato o tipo de documento subido no es compatible.",
        tips: [
          "Asegúrate de subir un DNI o pasaporte oficial válido emitido por un gobierno.",
        ],
      };
    case "consent_declined":
      return {
        title: "Consentimiento no otorgado",
        description: "No se otorgó el consentimiento requerido en Stripe para procesar la verificación.",
        tips: [
          "Al iniciar el proceso de verificación, acepta los términos de recogida de datos para poder validar tu identidad.",
        ],
      };
    default:
      return {
        title: "La verificación no se pudo completar",
        description: lastError.reason || "Hubo un inconveniente al validar los datos del documento.",
        tips: [
          "Asegúrate de que la fotografía sea nítida, sin sombras ni partes cortadas.",
          "Comprueba que el documento esté en vigor.",
        ],
      };
  }
}

export default function IdentityVerificationModal({ open }: IdentityVerificationModalProps) {
  const { refreshProfile, signOut, deleteUserAccount } = useAuth();
  const [phase, setPhase] = useState<Phase>("loading");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [lastVerificationError, setLastVerificationError] = useState<IdentityVerificationLastError | null>(null);
  const [consentGiven, setConsentGiven] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [showAbortConfirm, setShowAbortConfirm] = useState(false);
  const [isAborting, setIsAborting] = useState(false);
  const startedRef = useRef(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const readyCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const preventClose = useCallback((e: Event) => e.preventDefault(), []);

  const startVerification = useCallback(async () => {
    setPhase("loading");
    setErrorMsg(null);
    try {
      const { data, error } = await supabase.functions.invoke("create-identity-verification", {
        body: {
          require_biometric: true,
          level: "biometric",
        },
      });
      
      // Si recibimos un last_error previo de la sesión
      if (data?.last_error) {
        setLastVerificationError(data.last_error);
      }

      // --- Manejo mejorado de errores: leer FunctionsHttpError correctamente ---
      if (error) {
        let detailedErrorMsg = "No se pudo iniciar la verificación";

        if (error instanceof FunctionsHttpError && error.context.status === 401) {
          // JWT caducado (común en móvil con pantalla bloqueada): no mostrar
          // el mensaje crudo en inglés.
          detailedErrorMsg = paymentErrorMessage("session_expired");
        } else if (error instanceof FunctionsHttpError) {
          try {
            const errorBody = await error.context.json() as any;
            detailedErrorMsg = errorBody?.details
              ? `${errorBody?.error ?? "Error"}: ${errorBody.details}`
              : (errorBody?.error ?? errorBody?.message ?? detailedErrorMsg);
            console.error("[IdentityVerificationModal] FunctionsHttpError details:", errorBody);
          } catch (parseErr) {
            console.error("[IdentityVerificationModal] Error parsing FunctionsHttpError body:", parseErr);
            detailedErrorMsg = error.message || detailedErrorMsg;
          }
        } else {
          detailedErrorMsg = error.message || detailedErrorMsg;
          console.error("[IdentityVerificationModal] Non-HTTP error:", error);
        }

        reportPaymentError("identity_verify_error", error, { status: (error as any)?.status });
        setPhase("error");
        setErrorMsg(detailedErrorMsg);
        return;
      }
      
      if (!data?.client_secret) {
        if (data?.already_verified) {
          await refreshProfile?.();
          setPhase("success");
          return;
        }
        
        // Si no hay error pero tampoco hay client_secret, es un estado inesperado
        console.warn("[IdentityVerificationModal] No client_secret in response:", data);
        setPhase("error");
        setErrorMsg("La verificación no se pudo inicializar. Por favor, inténtalo de nuevo.");
        return;
      }

      setPhase("verifying");
      // Cargamos Stripe solo si la clave está realmente configurada
      // (detecta placeholders como pk_test_REEMPLAZAR_...).
      // getStripePromise nunca rechaza: si js.stripe.com no carga resuelve a null.
      const stripe = await getStripePromise(STRIPE_PUBLISHABLE_KEY);
      if (!stripe) {
        setPhase("error");
        setErrorMsg(
          isStripePublishableKeyConfigured(STRIPE_PUBLISHABLE_KEY)
            ? paymentErrorMessage("stripe_js_load")
            : "Falta la clave pública de Stripe (VITE_STRIPE_PUBLISHABLE_KEY). Configúrala en apps/web/.env.local."
        );
        return;
      }

      const result = await stripe.verifyIdentity(data.client_secret as string);

      if (result?.error) {
        // Distinción CRÍTICA (incidente móvil): solo la CANCELACIÓN explícita
        // del usuario (cerró la ventana) aborta el registro. Un error TÉCNICO
        // (red, navegador que bloquea el iframe cross-site, memoria) NO debe
        // borrar la cuenta: se muestra un error reintenable.
        console.warn("[IdentityVerificationModal] Verification error:", {
          code: result.error.code,
          message: result.error.message,
        });
        reportPaymentError("identity_verify_error", result.error, { code: result.error.code });

        if (result.error.code === "canceled") {
          // El usuario cerró la ventana de verificación → ABORTAR el registro
          await handleAbortProcess();
          return;
        }

        setPhase("error");
        setErrorMsg(paymentErrorMessage("identity_verify_error"));
        return;
      }

      // El usuario completó el flujo en el iframe local (on-device).
      // El webhook marca identity_verified de forma asíncrona.
      // Polling breve hasta confirmar (máx ~100s).
      setPhase("success");
      const pollResult = await pollForIdentityVerification();
      if (pollResult.verified) {
        await refreshProfile?.();
      } else {
        if (pollResult.lastError) {
          setLastVerificationError(pollResult.lastError);
        }
        setRetryCount((prev) => prev + 1);
        setPhase("ready");
      }
    } catch (err: any) {
      // Excepción inesperada (red, supabase, stripe.js): error visible
      // reintenable, nunca un hang ni un borrado de cuenta.
      console.error("[IdentityVerificationModal] Unexpected error:", err);
      reportPaymentError("identity_verify_error", err);
      setPhase("error");
      setErrorMsg(
        isUsableErrorMessage(err?.message)
          ? (err?.message as string)
          : paymentErrorMessage("identity_verify_error"),
      );
    }
  }, [refreshProfile]);

  // Manejar el click en el botón X (close button) del modal
  const handleCloseAttempt = useCallback((isOpen: boolean) => {
    // Si intenta cerrar (isOpen = false) y la sesión está activa
    if (!isOpen && !isAborting && phase !== "aborted") {
      // Mostrar confirmación antes de cerrar
      setShowAbortConfirm(true);
    }
  }, [isAborting, phase]);

  // Ejecutar abort: logout + delete account + redirect
  const handleAbortProcess = useCallback(async () => {
    setIsAborting(true);
    try {
      // Eliminar/desactivar la cuenta
      const { error } = await deleteUserAccount();
      if (error) {
        console.error("[IdentityVerificationModal] Error deleting account:", error);
      }
      
      // Cerrar sesión
      await signOut();
      
      // Mostrar pantalla de despedida brevemente
      setPhase("aborted");
      
      // Redirigir a brickshare.org después de 3 segundos
      setTimeout(() => {
        window.location.href = "https://brickshare.org";
      }, 3000);
    } catch (err) {
      console.error("[IdentityVerificationModal] Abort process failed:", err);
      setIsAborting(false);
    }
  }, [deleteUserAccount, signOut]);

  // Manejo de ENTER: permite activar botones de acción presionando ENTER
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !isAborting) {
      e.preventDefault();
      // Si es fase "ready" y consent está dado, hacer retry
      if (phase === "ready" && consentGiven) {
        startVerification();
      }
    }
  }, [phase, consentGiven, isAborting, startVerification]);

  // Watchdog de verificación: si verifyIdentity / el iframe de Stripe no
  // resuelve en 5 min (red lenta, cookies de terceros bloqueadas por la
  // «protección estricta» del navegador, iframe matado por memoria) → error
  // visible con reintento en vez de «Esperando a que completes la
  // verificación…» para siempre.
  useEffect(() => {
    if (phase !== "verifying") return;
    const t = setTimeout(() => {
      console.error("[IdentityVerificationModal] Watchdog verifyIdentity agotado (5 min)");
      reportPaymentError("identity_verify_timeout", new Error("verify_identity_watchdog_5min"));
      setPhase("error");
      setErrorMsg(paymentErrorMessage("identity_verify_timeout"));
    }, 5 * 60_000);
    return () => clearTimeout(t);
  }, [phase]);

  /**
   * Re-check periódico en fase "ready": si el webhook llega tarde (después de que
   * el polling inicial expire), lo detectamos y pasamos a "success" automáticamente.
   * Esto soluciona el incidente donde los webhooks tardaban >30s.
   * 
   * (documentación del incidente eliminada del repo 2026-09-15)
   */
  useEffect(() => {
    if (phase !== "ready") {
      // Limpiar intervalo si salimos de la fase "ready"
      if (readyCheckIntervalRef.current) {
        clearInterval(readyCheckIntervalRef.current);
        readyCheckIntervalRef.current = null;
      }
      return;
    }

    // Re-check cada 30s mientras estamos en fase "ready"
    readyCheckIntervalRef.current = setInterval(async () => {
      try {
        console.log("[IdentityVerificationModal] Periodic re-check in 'ready' phase...");
        const { data: check } = await supabase.functions.invoke("create-identity-verification", {
          body: {
            require_biometric: true,
            level: "biometric",
          },
        });
        if (check?.already_verified) {
          console.log("[IdentityVerificationModal] ✅ Late webhook detected! Identity is now verified.");
          await refreshProfile?.();
          setPhase("success");
          if (readyCheckIntervalRef.current) {
            clearInterval(readyCheckIntervalRef.current);
            readyCheckIntervalRef.current = null;
          }
        }
      } catch (err) {
        console.warn("[IdentityVerificationModal] Periodic re-check failed:", err);
      }
    }, 30000); // Re-check cada 30 segundos

    return () => {
      if (readyCheckIntervalRef.current) {
        clearInterval(readyCheckIntervalRef.current);
        readyCheckIntervalRef.current = null;
      }
    };
  }, [phase, refreshProfile]);

  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      if (readyCheckIntervalRef.current) {
        clearInterval(readyCheckIntervalRef.current);
        readyCheckIntervalRef.current = null;
      }
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;
    void startVerification();
  }, [open, startVerification]);

  return (
    <Dialog open={open} onOpenChange={handleCloseAttempt}>
      <DialogContent
        className="sm:max-w-[480px]"
        onInteractOutside={preventClose}
        onPointerDownOutside={preventClose}
        onKeyDown={handleKeyDown}
        ref={dialogRef}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Verifica tu identidad
          </DialogTitle>
          <DialogDescription>
            Para activar tu cuenta en Brickshare necesitamos verificar tu identidad
            con Stripe Identity. Solo necesitas tu documento de identidad (DNI o
            pasaporte); es un proceso obligatorio y seguro.
          </DialogDescription>
          
          {(phase === "ready" || phase === "error") && (
            <div className="mt-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <Checkbox 
                id="identity-consent" 
                checked={consentGiven}
                onCheckedChange={(checked) => setConsentGiven(!!checked)}
                className="mt-0.5"
              />
              <Label htmlFor="identity-consent" className="text-xs leading-tight cursor-pointer text-foreground">
                <strong>Consiento expresamente</strong> que Brickshare y su proveedor Stripe, Inc. 
                procesen las imágenes de mi documento de identidad y, en su caso, mi selfie (datos 
                biométricos) para verificar mi identidad, prevenir el fraude y cumplir con la 
                normativa KYC/AML. He leído la{" "}
                <a href="/privacidad#verificacion-identidad" className="text-primary underline" target="_blank" rel="noopener noreferrer">
                  sección de verificación de identidad
                </a>{" "}
                en la Política de Privacidad.
              </Label>
            </div>
          )}
        </DialogHeader>

        <div className="py-4">
          {phase === "loading" && (
            <div className="flex items-center justify-center gap-3 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              Preparando la verificación...
            </div>
          )}

          {phase === "verifying" && (
            <div className="flex items-center justify-center gap-3 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              Esperando a que completes la verificación...
            </div>
          )}

          {phase === "success" && (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <ShieldCheck className="h-10 w-10 text-emerald-500" />
              <p className="text-sm font-medium">Verificación enviada</p>
              <p className="text-xs text-muted-foreground">
                Estamos confirmando tu identidad. En unos segundos podrás continuar con el registro.
              </p>
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}

          {phase === "ready" && (() => {
            const errorHelp = getIdentityErrorHelp(lastVerificationError);
            return (
              <div className="flex flex-col items-center gap-4 py-4 text-center">
                {errorHelp ? (
                  <div className="w-full text-left bg-amber-50/90 border border-amber-200 rounded-lg p-3.5 text-xs text-amber-950 space-y-2">
                    <div className="flex items-center gap-2 font-semibold text-amber-900">
                      <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                      <span>{errorHelp.title}</span>
                    </div>
                    <p className="text-amber-800 leading-relaxed">
                      {errorHelp.description}
                    </p>
                    {errorHelp.tips && errorHelp.tips.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-amber-200/70">
                        <p className="font-semibold text-amber-900 mb-1 flex items-center gap-1.5">
                          <Camera className="h-3.5 w-3.5 text-amber-700" />
                          Consejos para el reintento:
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-amber-800">
                          {errorHelp.tips.map((tip, idx) => (
                            <li key={idx} className="leading-snug">{tip}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    La verificación no se completó. Puedes volver a intentarlo cuando quieras.
                  </p>
                )}

                <Button 
                  onClick={startVerification} 
                  className="w-full" 
                  disabled={!consentGiven}
                  data-testid="identity-retry-button"
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Iniciar verificación de nuevo
                </Button>
                
                <Button
                  onClick={() => setShowAbortConfirm(true)}
                  variant="outline"
                  className="w-full text-destructive hover:text-destructive"
                  disabled={isAborting}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  No deseo verificarme
                </Button>
                
                {retryCount >= 2 && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs w-full text-left">
                    <p className="font-semibold text-blue-900">¿Problemas con la verificación automática?</p>
                    <p className="text-blue-700 mt-1">
                      Si tu documento no es reconocido, contacta con nuestro equipo en{" "}
                      <a href="mailto:info@brickshare.org" className="underline font-medium">
                        info@brickshare.org
                      </a>{" "}
                      y lo revisaremos manualmente en menos de 24 horas.
                    </p>
                  </div>
                )}
              </div>
            );
          })()}

          {phase === "error" && (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <AlertTriangle className="h-8 w-8 text-destructive" />
              <p className="text-sm text-destructive">{errorMsg}</p>
              <Button 
                onClick={startVerification} 
                variant="outline" 
                className="w-full"
                disabled={!consentGiven}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Reintentar
              </Button>
              
              {retryCount >= 2 && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs w-full">
                  <p className="font-semibold text-blue-900">¿Problemas con la verificación automática?</p>
                  <p className="text-blue-700 mt-1">
                    Si tu documento no es reconocido, contacta con nuestro equipo en{" "}
                    <a href="mailto:info@brickshare.org" className="underline font-medium">
                      info@brickshare.org
                    </a>{" "}
                    y lo revisaremos manualmente en menos de 24 horas.
                  </p>
                </div>
              )}
            </div>
          )}

          {phase === "aborted" && (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <AlertTriangle className="h-10 w-10 text-amber-500" />
              <p className="text-sm font-medium">Proceso cancelado</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Verificamos la identidad de todos los miembros de la comunidad para fortalecer 
                los mecanismos de seguridad de los miembros de ésta, cumpliendo con la normativa 
                vigente de custodia de datos personales.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Redirigiendo a Brickshare...
              </p>
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mt-2" />
            </div>
          )}

          {phase === "ready" || phase === "error" ? (
            <p className="text-[11px] text-muted-foreground text-center mt-2">
              Stripe Identity solo valida el documento. No almacenamos tus datos de identidad
              en nuestros servidores.
            </p>
          ) : null}
        </div>
      </DialogContent>

      {/* Dialog de confirmación para abort */}
      <AlertDialog open={showAbortConfirm} onOpenChange={setShowAbortConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Si no deseas verificar tu identidad, tu cuenta será desactivada y no podrás 
              acceder a Brickshare. La verificación de identidad es obligatoria para usar 
              nuestra plataforma.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-3">
            <AlertDialogCancel disabled={isAborting}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAbortProcess}
              disabled={isAborting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isAborting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cancelando...
                </>
              ) : (
                <>
                  <LogOut className="mr-2 h-4 w-4" />
                  Abandonar
                </>
              )}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}